/**
 * CHAMADOS — o suporte da Cert4All, pela janela do Hugi (ADR-0019).
 *
 * ## O que esta tela é, e o que ela não é
 *
 * O chamado nasce, mora e é respondido na Cert4All. Esta tela coleta o texto e mostra a conversa; o
 * SERVIDOR do Hugi repassa (a chave do módulo nunca chega aqui). Prioridade e status são do
 * atendimento: aqui só se leem — todo chamado nasce com prioridade normal, e quem atende ajusta.
 *
 * ⚠️ Não é o "Acesso de suporte" do cabeçalho (S11, `Suporte.tsx`), que é o operador da plataforma
 * abrindo o painel de um cliente. Nomes parecidos, capacidades opostas.
 *
 * ## Três regras que custaram caro em outro lugar
 *
 * 1. **Indisponível NUNCA vira lista vazia.** Um 503 mostra "tente de novo"; "nenhum chamado" só
 *    aparece com uma lista vazia de verdade. Caixa vazia para quem tem cinco abertos é o defeito que o
 *    S12 inteiro foi desenhado para não ter.
 * 2. **A chave de idempotência é do RASCUNHO** (`rascunho-de-chamado.ts`): tentar de novo depois de
 *    uma falha reusa a mesma chave, e a Cert4All devolve o chamado que já existe em vez de abrir outro.
 *    Por isso a falha NÃO limpa o formulário.
 * 3. **A recusa aparece com a frase do servidor.** Quem não é administrador nem do grupo Tecnologia lê
 *    exatamente isso — e a quem pedir.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { LifeBuoy, Lock, Plus, Send } from "lucide-react";
import { DetalheTecnico, EstadoDaFalha, chamar, falhaDe, type FalhaDaApi } from "@hugi/ui";
import {
  Carregando,
  CampoDoCadastro,
  Dica,
  GradeDeCartoes,
  ListView,
  PaginaInteira,
  Selo,
  Vazio,
  type CartaoDaGrade,
  type ColunaDaLista,
  type Tom,
} from "@hugi/ui/kit";
import { chaveDoRascunho, esquecerRascunho } from "./rascunho-de-chamado.js";
import "./chamados.css";

interface Chamado {
  readonly id: string;
  readonly titulo: string;
  readonly descricao: string;
  readonly categoria: string;
  readonly prioridade: string;
  readonly status: string;
  readonly criadoEm: string;
  readonly atualizadoEm: string;
  readonly ultimaAtividadeEm: string;
  readonly resolvidoEm: string | null;
}

interface Mensagem {
  readonly id: string;
  readonly mensagem: string;
  readonly criadoEm: string;
  readonly autor: "solicitante" | "atendimento";
}

interface Lista {
  readonly chamados: readonly Chamado[];
  readonly pagina: number;
  readonly limite: number;
  readonly temMais: boolean;
}

interface Detalhe {
  readonly chamado: Chamado;
  readonly mensagens: readonly Mensagem[];
  readonly temMaisMensagens: boolean;
}

interface PaginaDeMensagens {
  readonly mensagens: readonly Mensagem[];
  readonly temMais: boolean;
}

/** As categorias do S12, na ordem em que alguém as procura: o problema técnico primeiro. */
const CATEGORIAS = [
  { id: "technical", rotulo: "Problema técnico" },
  { id: "bug", rotulo: "Erro no sistema" },
  { id: "access", rotulo: "Acesso" },
  { id: "billing", rotulo: "Cobrança" },
  { id: "other", rotulo: "Outro" },
] as const;

type Categoria = (typeof CATEGORIAS)[number]["id"];

const ROTULO_STATUS: Readonly<Record<string, string>> = {
  open: "Aberto",
  waiting_response: "Aguardando sua resposta",
  in_progress: "Em atendimento",
  escalated: "Escalado",
  resolved: "Resolvido",
  closed: "Fechado",
};

const ROTULO_PRIORIDADE: Readonly<Record<string, string>> = {
  low: "baixa",
  medium: "normal",
  high: "alta",
  urgent: "urgente",
};

// Valor que o atendimento acrescente amanhã aparece cru, e não some.
const rotulo = (mapa: Readonly<Record<string, string>>, valor: string) => mapa[valor] ?? valor;
const rotuloDaCategoria = (valor: string) => CATEGORIAS.find((c) => c.id === valor)?.rotulo ?? valor;

function quando(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(d);
}

/** A falha classificada — e erro que não veio da API (bug da tela) não vira `null` silencioso. */
function falhaDoErro(erro: unknown): FalhaDaApi {
  return (
    falhaDe(erro) ?? {
      tipo: "inesperado",
      erro: { erro: "ERRO_NA_TELA", mensagem: erro instanceof Error ? erro.message : String(erro) },
      status: 0,
    }
  );
}

const CHAVE = ["chamados"] as const;
const ESCOPO_NOVO = "novo-chamado";


/** O tom de cada situação no `Selo` (cor E texto; o texto continua o mesmo de sempre). */
const TOM_STATUS: Readonly<Record<string, Tom>> = {
  open: "info",
  waiting_response: "atencao",
  in_progress: "info",
  escalated: "atencao",
  resolved: "sucesso",
  closed: "neutro",
};
const TOM_PRIORIDADE: Readonly<Record<string, Tom>> = { low: "neutro", medium: "neutro", high: "atencao", urgent: "perigo" };

const dataCurta = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }).format(d);
};

/**
 * A falha, no estado certo.
 *
 * `EstadoDaFalha` resolve quase tudo, mas um 403 dele vira "falta a permissão X" — e a recusa da
 * Cert4All (`SEM_PERMISSAO_DE_CHAMADO`) não tem permissão do Hugi para nomear: ela tem uma FRASE, que
 * diz a quem pedir. Recusa de regra mostra a frase e não oferece "tentar de novo" (repetir não muda a
 * regra); indisponibilidade oferece, pelo `EstadoDaFalha`.
 */
function FalhaDeChamado(props: { readonly falha: FalhaDaApi; readonly aoRepetir?: () => void }) {
  const { falha } = props;
  const titulo =
    falha.tipo === "sem_permissao" && falha.erro.erro !== "SEM_PERMISSAO"
      ? "Sem acesso aos chamados"
      : falha.tipo === "nao_encontrado"
        ? "Chamado não encontrado"
        : falha.tipo === "recusado" || falha.tipo === "sem_sessao"
          ? "Não foi possível seguir"
          : null;

  if (titulo === null) {
    return <EstadoDaFalha falha={falha} {...(props.aoRepetir ? { aoRepetir: props.aoRepetir } : {})} />;
  }
  return (
    <div role="alert" className="hugi-chamados-recusa">
      <p className="hugi-chamados-recusa-titulo">
        <Lock size={16} aria-hidden="true" />
        {titulo}
      </p>
      <p>{falha.erro.mensagem ?? "A Cert4All recusou o pedido."}</p>
      <DetalheTecnico erro={falha.erro.erro} traceId={falha.erro.traceId} />
    </div>
  );
}

/** A frase de uma falha de ENVIO, logo ao lado do botão — e o formulário continua preenchido. */
function FalhaDoEnvio(props: { readonly falha: FalhaDaApi; readonly padrao: string }) {
  return (
    <div role="alert" className="hugi-chamados-erro">
      <p>{props.falha.erro.mensagem ?? props.padrao}</p>
      <DetalheTecnico erro={props.falha.erro.erro} traceId={props.falha.erro.traceId} />
    </div>
  );
}

/**
 * A TELA: Consulta em DUAS colunas (spec 22; como os Chamados do Stonen). A lista à esquerda; à direita,
 * o formulário de abrir chamado (em `/chamados`) ou a conversa do chamado aberto (`/chamados/<id>`).
 * A página não rola: a lista, o formulário e o fio rolam cada um por dentro, e o botão de cada cartão
 * fica preso no rodapé dele. No celular, um cartão por vez (o seletor do kit).
 */
function Console(props: { readonly selecionado: string | null; readonly direita: CartaoDaGrade }) {
  const [ativo, setAtivo] = useState(props.selecionado ? props.direita.id : "lista");
  const cartoes: CartaoDaGrade[] = [
    {
      id: "lista",
      titulo: "Meus chamados",
      rotuloCurto: "Chamados",
      area: "lista",
      proprio: true,
      conteudo: <MeusChamados selecionado={props.selecionado} comAbrir={props.selecionado !== null} />,
    },
    { ...props.direita, area: "direita" },
  ];
  return (
    <PaginaInteira rotulo="Chamados" classe="hugi-chamados">
      <GradeDeCartoes
        rotulo="Chamados"
        cartoes={cartoes}
        areas={'"lista direita"'}
        colunas="minmax(0, 3fr) minmax(0, 2fr)"
        linhas="minmax(0, 1fr)"
        ativo={ativo}
        aoTrocar={setAtivo}
      />
    </PaginaInteira>
  );
}

export function Chamados() {
  const novo = useNovoChamado();
  return <Console selecionado={null} direita={novo} />;
}

function useNovoChamado(): CartaoDaGrade {
  const clienteDeConsulta = useQueryClient();
  const [, navegar] = useLocation();
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [categoria, setCategoria] = useState<Categoria>("technical");
  const [revisar, setRevisar] = useState<string | null>(null);
  const [aberto, setAberto] = useState<{ readonly id: string; readonly jaEstavaFeito: boolean } | null>(null);

  const abrir = useMutation({
    mutationFn: (corpo: { titulo: string; descricao: string; categoria: Categoria; chaveDeIdempotencia: string }) =>
      chamar<{ chamado: Chamado; jaEstavaFeito: boolean }>("/api/painel/chamados", { metodo: "POST", corpo }),
    onSuccess: (r) => {
      // Só no SUCESSO o rascunho é esquecido. A falha guarda texto e chave, e o "tentar de novo" reusa os dois.
      esquecerRascunho(ESCOPO_NOVO);
      setTitulo("");
      setDescricao("");
      setAberto({ id: r.chamado.id, jaEstavaFeito: r.jaEstavaFeito });
      void clienteDeConsulta.invalidateQueries({ queryKey: CHAVE });
    },
  });

  function enviar() {
    setAberto(null);
    const conteudo = { titulo: titulo.trim(), descricao: descricao.trim(), categoria };
    if (conteudo.titulo.length < 5 || conteudo.descricao.length < 10) {
      setRevisar("Revise o chamado: assunto com pelo menos 5 caracteres e descrição com pelo menos 10.");
      return;
    }
    setRevisar(null);
    abrir.mutate({ ...conteudo, chaveDeIdempotencia: chaveDoRascunho(ESCOPO_NOVO, conteudo) });
  }

  const falha = abrir.isError ? falhaDoErro(abrir.error) : null;

  return {
    id: "novo",
    titulo: "Abrir chamado",
    rotuloCurto: "Abrir",
    dica: "Fale com o suporte da Cert4All: a resposta chega aqui. Todo chamado nasce com prioridade normal, e quem atende ajusta.",
    conteudo: (
      <div className="hugi-chamados-form">
        <CampoDoCadastro rotulo="Assunto">
          {(p) => (
            <input
              {...p}
              value={titulo}
              maxLength={200}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: o QR do WhatsApp não aparece"
            />
          )}
        </CampoDoCadastro>
        <CampoDoCadastro rotulo="Tipo">
          {(p) => (
            <select {...p} value={categoria} onChange={(e) => setCategoria(e.target.value as Categoria)}>
              {CATEGORIAS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.rotulo}
                </option>
              ))}
            </select>
          )}
        </CampoDoCadastro>
        <CampoDoCadastro rotulo="O que aconteceu" largo>
          {(p) => (
            <textarea
              {...p}
              rows={6}
              maxLength={5000}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="O que você fez, o que esperava e o que apareceu."
            />
          )}
        </CampoDoCadastro>
      </div>
    ),
    rodape: (
      <div className="hugi-chamados-rodape">
        <div className="hugi-chamados-retorno">
          {revisar ? (
            <p role="alert" className="hugi-chamados-erro">
              {revisar}
            </p>
          ) : null}
          {falha ? <FalhaDoEnvio falha={falha} padrao="Não foi possível abrir o chamado." /> : null}
          {aberto ? (
            <p role="status" className="hugi-chamados-ok">
              {aberto.jaEstavaFeito
                ? "Este chamado já tinha sido aberto — nada foi duplicado."
                : "Chamado aberto. A resposta do atendimento aparece nele."}{" "}
              <button
                type="button"
                className="v2-link"
                onClick={() => navegar(`/chamados/${encodeURIComponent(aberto.id)}`)}
              >
                Ver o chamado
              </button>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="v2-botao is-primario"
          disabled={abrir.isPending}
          title={abrir.isPending ? "Enviando o chamado." : undefined}
          onClick={enviar}
        >
          {abrir.isPending ? "Enviando…" : falha?.tipo === "indisponivel" ? "Tentar de novo" : "Abrir chamado"}
        </button>
      </div>
    ),
  };
}

const COLUNAS: ColunaDaLista<Chamado>[] = [
  { id: "assunto", rotulo: "Assunto", principal: true, link: true, dica: (c) => c.titulo, celula: (c) => c.titulo },
  {
    id: "estado",
    rotulo: "Situação",
    ordenar: (c) => rotulo(ROTULO_STATUS, c.status),
    celula: (c) => <Selo tom={TOM_STATUS[c.status] ?? "neutro"}>{rotulo(ROTULO_STATUS, c.status)}</Selo>,
  },
  { id: "tipo", rotulo: "Tipo", ordenar: (c) => rotuloDaCategoria(c.categoria), celula: (c) => rotuloDaCategoria(c.categoria) },
  {
    id: "atividade",
    rotulo: "Última atividade",
    numerica: true,
    dica: (c) => quando(c.ultimaAtividadeEm),
    ordenar: (c) => new Date(c.ultimaAtividadeEm).getTime() || null,
    celula: (c) => dataCurta(c.ultimaAtividadeEm),
  },
];

function MeusChamados(props: { readonly selecionado: string | null; readonly comAbrir: boolean }) {
  const [, navegar] = useLocation();
  const [pagina, setPagina] = useState(1);
  const lista = useQuery({
    queryKey: [...CHAVE, "lista", pagina],
    queryFn: () => chamar<Lista>(`/api/painel/chamados?pagina=${pagina}&limite=20`),
  });

  if (lista.isError) {
    // Indisponível cai aqui — e é "tente de novo", nunca a lista vazia.
    return (
      <div className="v2-cartao hugi-chamados-falha">
        <header className="v2-lista-cabecalho">
          <span className="v2-icone-objeto" aria-hidden="true">
            <LifeBuoy size={16} />
          </span>
          <div className="v2-lista-identidade is-sem-vista">
            <h1>Chamados</h1>
          </div>
        </header>
        <div className="hugi-chamados-falha-corpo">
          <FalhaDeChamado falha={falhaDoErro(lista.error)} aoRepetir={() => void lista.refetch()} />
        </div>
      </div>
    );
  }

  const temPaginas = lista.data !== undefined && (pagina > 1 || lista.data.temMais);
  return (
    <ListView<Chamado>
      titulo="Chamados"
      icone={<LifeBuoy size={16} />}
      classe="hugi-chamados-lista"
      densidade={false}
      colunas={COLUNAS}
      linhas={lista.data ? [...lista.data.chamados] : undefined}
      chave={(c) => c.id}
      estado={{ carregando: lista.isPending }}
      ordemInicial={{ coluna: "atividade", desc: true }}
      selecao={{ selecionada: props.selecionado, aoSelecionar: () => undefined }}
      abrir={{
        href: (c) => `/chamados/${encodeURIComponent(c.id)}`,
        aoAbrir: (c) => navegar(`/chamados/${encodeURIComponent(c.id)}`),
      }}
      contagem={(n) => `${n === 1 ? "1 chamado" : `${n} chamados`}${temPaginas ? ` · página ${pagina}` : ""}`}
      vazio={{
        semNada: pagina === 1 ? "Nenhum chamado aberto por você" : "Não há chamados mais antigos.",
        semResultado: "Nenhum chamado nesta vista.",
      }}
      acoes={
        <>
          <Dica texto="Cada pessoa vê os chamados que ela abriu, com a resposta do atendimento. Chamados de outras pessoas da empresa não aparecem." />
          {temPaginas ? (
            <>
              <button
                type="button"
                className="v2-botao is-pequeno"
                disabled={pagina === 1}
                title={pagina === 1 ? "Esta já é a página mais recente." : undefined}
                onClick={() => setPagina((p) => p - 1)}
              >
                Mais recentes
              </button>
              <button
                type="button"
                className="v2-botao is-pequeno"
                disabled={!lista.data?.temMais}
                title={!lista.data?.temMais ? "Não há chamados mais antigos." : undefined}
                onClick={() => setPagina((p) => p + 1)}
              >
                Mais antigos
              </button>
            </>
          ) : null}
          {props.comAbrir ? (
            <button type="button" className="v2-botao is-primario is-pequeno" onClick={() => navegar("/chamados")}>
              <Plus size={14} aria-hidden="true" />
              Abrir chamado
            </button>
          ) : null}
        </>
      }
    />
  );
}

/** A página de um chamado (`/chamados/<id>`): a mesma consulta, com a conversa na coluna da direita. */
export function ChamadoDetalhe(props: { readonly id: string }) {
  return <ChamadoAberto key={props.id} id={props.id} />;
}

function ChamadoAberto(props: { readonly id: string }) {
  const detalhe = useQuery({
    queryKey: [...CHAVE, "um", props.id],
    queryFn: () => chamar<Detalhe>(`/api/painel/chamados/${encodeURIComponent(props.id)}`),
  });
  const conversa = useConversa(props.id, detalhe.data);

  let direita: CartaoDaGrade;
  if (detalhe.isError) {
    direita = {
      id: "chamado",
      titulo: "Chamado",
      conteudo: <FalhaDeChamado falha={falhaDoErro(detalhe.error)} aoRepetir={() => void detalhe.refetch()} />,
    };
  } else if (conversa) {
    direita = conversa;
  } else {
    direita = { id: "chamado", titulo: "Chamado", conteudo: <Carregando>Carregando o chamado…</Carregando> };
  }
  return <Console selecionado={props.id} direita={direita} />;
}

function useConversa(id: string, dados: Detalhe | undefined): CartaoDaGrade | null {
  const clienteDeConsulta = useQueryClient();
  const [historico, setHistorico] = useState<readonly Mensagem[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [falhaDoHistorico, setFalhaDoHistorico] = useState<FalhaDaApi | null>(null);
  const [texto, setTexto] = useState("");
  const [revisar, setRevisar] = useState<string | null>(null);
  // O chamado entra no escopo: a mesma chave apontando para outro chamado é conflito lá.
  const escopo = `comentario:${id}`;
  const caminho = `/api/painel/chamados/${encodeURIComponent(id)}`;

  const comentar = useMutation({
    mutationFn: (corpo: { mensagem: string; chaveDeIdempotencia: string }) =>
      chamar<{ mensagem: Mensagem; jaEstavaFeito: boolean }>(`${caminho}/comentarios`, { metodo: "POST", corpo }),
    onSuccess: () => {
      esquecerRascunho(escopo);
      setTexto("");
      setHistorico(null);
      void clienteDeConsulta.invalidateQueries({ queryKey: CHAVE });
    },
  });

  if (!dados) return null;
  const { chamado, mensagens, temMaisMensagens } = dados;

  /*
    O detalhe traz as 200 mais recentes e diz `temMaisMensagens`. O resto vem de `.../mensagens`, que
    anda do COMEÇO para a frente — a única direção em que uma mensagem nova chegando durante a leitura
    não empurra uma antiga para a página seguinte. Teto de 50 páginas: 10.000 mensagens é conversa
    que ninguém lê numa tela, e laço sem teto é laço que um defeito de `temMais` não deixa acabar.
  */
  async function carregarHistorico() {
    setCarregando(true);
    setFalhaDoHistorico(null);
    try {
      const todas: Mensagem[] = [];
      for (let pagina = 1; pagina <= 50; pagina += 1) {
        const parte = await chamar<PaginaDeMensagens>(`${caminho}/mensagens?pagina=${pagina}&limite=200`);
        todas.push(...parte.mensagens);
        if (!parte.temMais) break;
      }
      setHistorico(todas);
    } catch (erro) {
      setFalhaDoHistorico(falhaDoErro(erro));
    } finally {
      setCarregando(false);
    }
  }

  function enviar() {
    const conteudo = { mensagem: texto.trim() };
    if (!conteudo.mensagem) {
      setRevisar("Escreva a resposta antes de enviar.");
      return;
    }
    setRevisar(null);
    comentar.mutate({ ...conteudo, chaveDeIdempotencia: chaveDoRascunho(escopo, conteudo) });
  }

  /*
    Em ordem de CONVERSA (a mais antiga em cima), ordenada AQUI e não confiada à origem: o detalhe e o
    histórico saem de consultas diferentes lá, e o S12 avisa que juntar as duas sem olhar a ordem monta
    a conversa de trás para a frente.
  */
  const exibidas = [...(historico ?? mensagens)].sort((a, b) => a.criadoEm.localeCompare(b.criadoEm));
  const falha = comentar.isError ? falhaDoErro(comentar.error) : null;

  return {
    id: "chamado",
    titulo: chamado.titulo,
    rotuloCurto: "Chamado",
    ferramentas: <Selo tom={TOM_STATUS[chamado.status] ?? "neutro"}>{rotulo(ROTULO_STATUS, chamado.status)}</Selo>,
    conteudo: (
      <div className="hugi-chamados-fio">
        <dl className="v2-campos hugi-chamados-campos">
          <div>
            <dt>Tipo</dt>
            <dd>{rotuloDaCategoria(chamado.categoria)}</dd>
          </div>
          <div>
            <dt>Prioridade</dt>
            <dd>
              <Selo tom={TOM_PRIORIDADE[chamado.prioridade] ?? "neutro"}>
                {rotulo(ROTULO_PRIORIDADE, chamado.prioridade)}
              </Selo>
            </dd>
          </div>
          <div>
            <dt>Aberto em</dt>
            <dd>{quando(chamado.criadoEm)}</dd>
          </div>
          <div>
            <dt>Última atividade</dt>
            <dd>{quando(chamado.ultimaAtividadeEm)}</dd>
          </div>
        </dl>
        <section className="hugi-chamados-descricao" aria-labelledby="chamado-descricao">
          <h3 id="chamado-descricao">O que você escreveu</h3>
          <p>{chamado.descricao}</p>
        </section>
        {temMaisMensagens && !historico ? (
          <div className="hugi-chamados-faixa">
            <span>Mostrando as mensagens mais recentes.</span>
            <button
              type="button"
              className="v2-botao is-pequeno"
              disabled={carregando}
              title={carregando ? "Carregando o histórico." : undefined}
              onClick={() => void carregarHistorico()}
            >
              {carregando ? "Carregando…" : "Ver o histórico completo"}
            </button>
          </div>
        ) : null}
        {falhaDoHistorico ? <FalhaDeChamado falha={falhaDoHistorico} /> : null}
        {exibidas.length === 0 ? (
          <Vazio>Ainda sem respostas. Quando o atendimento responder, a mensagem aparece aqui.</Vazio>
        ) : (
          <ol className="hugi-chamados-mensagens" aria-label="Conversa">
            {exibidas.map((m) => (
              <li key={m.id} className={m.autor === "atendimento" ? "is-atendimento" : "is-voce"}>
                <span className="hugi-chamados-autor">
                  {m.autor === "atendimento" ? "Atendimento Cert4All" : "Você"} · {quando(m.criadoEm)}
                </span>
                <p>{m.mensagem}</p>
              </li>
            ))}
          </ol>
        )}
      </div>
    ),
    rodape: (
      <div className="hugi-chamados-resposta">
        <CampoDoCadastro rotulo="Responder">
          {(p) => <textarea {...p} rows={2} maxLength={5000} value={texto} onChange={(e) => setTexto(e.target.value)} />}
        </CampoDoCadastro>
        <div className="hugi-chamados-rodape">
          <div className="hugi-chamados-retorno">
            {revisar ? (
              <p role="alert" className="hugi-chamados-erro">
                {revisar}
              </p>
            ) : null}
            {falha ? <FalhaDoEnvio falha={falha} padrao="Não foi possível enviar a resposta." /> : null}
          </div>
          <button
            type="button"
            className="v2-botao is-primario"
            disabled={comentar.isPending}
            title={comentar.isPending ? "Enviando a resposta." : undefined}
            onClick={enviar}
          >
            <Send size={14} aria-hidden="true" />
            {comentar.isPending ? "Enviando…" : falha?.tipo === "indisponivel" ? "Tentar de novo" : "Enviar"}
          </button>
        </div>
      </div>
    ),
  };
}
